import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@material-ui/core";
import { getPageConfiguration } from "./config/pageConfigurations";
import {
  fetchDetailPageData,
  sortDetailPageData,
  changeDetailPagePage,
  changeDetailPageRowsPerPage,
} from "./redux/detailPageActions";
import {
  transformToTableData,
  GET_LOCATION_SUMMARY,
  PUBLIC_ACCESS_REVOKE_USER,
  GET_DATA_GUARD_FILTERS_LIST,
  CLEAR_DATA_GUARD_FILTERS,
  DATA_GUARD_NOTIFICATION_TOGGLE,
  ALERT_ACTION_REQUEST_DATAGUARD,
  ACTION_NOT_NEEDED,
} from "../Utility/DataguardConstant";
import DetailPageHeader from "./DetailPageHeader";
import ExpandableTableRow from "./ExpandedTableRow";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import DetailsPopup from "../Popup/Popup";
import "./DetailsPage.scss";
import { getSortValue, renderRevokePopup, renderNotificationPopup, renderAlertActionPopup } from "../DataGuardCommonComponents";
import { Popover } from "material-ui-core";
import DataGuardFilter from "./DataGuardFilter";
import { DataGuardAlertStatusSnackBar } from "../DataGuardHelperComponents";
import { type } from "@amcharts/amcharts4/core";

const DetailPage = (props) => {
  const selectedCategory = props.match.params.name;
  const dispatch = useDispatch();
  const summaryLoader = useSelector(
    (state) => state.DataguardReducer.locationSummaryLoader
  );
  const notificationStatus = useSelector(
    (state) => state.DataguardReducer.notificationUpdateStatus
  );
  const revokeAccessUpdateStatus = useSelector(
    (state) => state.DataguardReducer.revokeAccessUpdateStatus
  );

  const { data, loading, error } = useSelector(
    (state) => state.DetailPageReducer || {}
  );

  const loader = loading || summaryLoader;

  const fromTimeStamp = useSelector(
    (state) => state.DataguardReducer.fromTimeStamp
  );
  const toTimeStamp = useSelector(
    (state) => state.DataguardReducer.toTimeStamp
  );

  const isIncognito = useSelector((state) => state.DataguardReducer.incognito);
  const locationFiltersList = useSelector(
    (state) => state.DataguardReducer.selectedLocationFiltersList
  );

  const policySubtypeFiltersList = useSelector(
    (state) => state.DataguardReducer.selectedPolicySubtypeFiltersList
  );

  const severityFiltersList = useSelector(
    (state) => state.DataguardReducer.selectedSeverityFiltersList
  );
  const userRolesFiltersList = useSelector(
    (state) => state.DataguardReducer.selectedUserRolesFiltersList
  );
  const ownersFiltersList = useSelector(
    (state) => state.DataguardReducer.selectedOwnersFiltersList
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const [revokePopup, setRevokePopup] = useState(false);
  const [revokeDetails, setRevokeDetails] = useState(null);
  const [notificationPopup, setNotificationPopup] = useState(false);
  const [notificationDetails, setNotificationDetails] = useState(null);
  const [alertActionPopup, setAlertActionPopup] = useState(false);
  const [alertActionDetails, setAlertActionDetails] = useState(null);
  const [columnId, setColumnId] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumnField, setFilterColumnField] = useState(null);
  const [activeFilterField, setActiveFilterField] = useState(null);

  const tableData = data?.content || data?.roles;
  const handleFilterIconClick = (event, columnField) => {
    event.stopPropagation();
    const fieldMap = {
      policy: "policyName",
      severity: "severity",
      location: "location",
      users: "roleName",
      owner: "ownerName",
    };

    const normalizedField = fieldMap[columnField] || columnField;
    setFilterAnchorEl(event.currentTarget);
    setFilterColumnField(normalizedField);
    setActiveFilterField(normalizedField);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setFilterColumnField(null);
    setActiveFilterField(null);
  };
  const config = useMemo(
    () =>
      getPageConfiguration(
        selectedCategory,
        setRevokeDetails,
        setRevokePopup,
        dispatch,
        isIncognito,
        locationFiltersList,
        policySubtypeFiltersList,
        severityFiltersList,
        userRolesFiltersList,
        ownersFiltersList,
        handleFilterIconClick,
        activeFilterField,
        isIncognito,
        setNotificationDetails,
        setNotificationPopup,
        setAlertActionDetails,
        setAlertActionPopup
      ),
    [
      selectedCategory,
      revokeAccessUpdateStatus,
      dispatch,
      isIncognito,
      locationFiltersList,
      policySubtypeFiltersList,
      severityFiltersList,
      userRolesFiltersList,
      ownersFiltersList,
    ]
  );

  const transformedData = useMemo(
    () => transformToTableData(tableData, selectedCategory),
    [tableData, selectedCategory]
  );

  const handleRevokePopupClose = () => {
    setRevokePopup(false);
  };

  const handleRevokeConfirm = () => {
    if (!revokeDetails?.isDisabled) {
      dispatch({
        type: PUBLIC_ACCESS_REVOKE_USER,
        payload: {
          dataSourceId: revokeDetails?.dataSourceId,
          userEmailId: revokeDetails?.ownerId,
        },
      });
    }
    setRevokePopup(false);
  };

  const handleNotificationPopupClose = () => {
    setNotificationPopup(false);
  };

  const handleNotificationConfirm = () => {
    if (notificationDetails) {
      dispatch({
        type: DATA_GUARD_NOTIFICATION_TOGGLE,
        payload: {
          state: !notificationDetails?.currentState,
          data: notificationDetails?.policyId,
        },
      });
    }
    setNotificationPopup(false);
  };

  const handleAlertActionPopupClose = () => {
    setAlertActionPopup(false);
  };

  const handleAlertActionConfirm = (actionType) => {
    if (alertActionDetails) {
      dispatch({
        type: "ALERT_ACTION_REQUEST_DATAGUARD",
        payload: {
          actionType,
          policyId: alertActionDetails?.policyId,
        },
      });
    }
    setAlertActionPopup(false);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return transformedData;

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return [...transformedData].sort((a, b) => {
      const aValue = getSortValue(a[orderBy], orderBy);
      const bValue = getSortValue(b[orderBy], orderBy);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      const result = collator.compare(String(aValue), String(bValue));
      return order === "asc" ? result : -result;
    });
  }, [transformedData, order, orderBy]);

  useEffect(() => {
    if (!config) return;

    const noFiltersApplied = [
      locationFiltersList,
      policySubtypeFiltersList,
      severityFiltersList,
      userRolesFiltersList,
      ownersFiltersList,
    ].every((list) => list.length === 0);

    if (!noFiltersApplied) return;

    dispatch(
      fetchDetailPageData(selectedCategory, config.apiEndpoint, {
        fromTimeStamp,
        toTimeStamp,
        page,
        rowsPerPage,
      })
    );

    dispatch({
      type: GET_LOCATION_SUMMARY,
      payload: { fromTimeStamp, toTimeStamp },
    });

    dispatch({
      type: GET_DATA_GUARD_FILTERS_LIST,
      payload: {
        filterEndpoint: config.FiltersListEndPoint,
        filterType: selectedCategory,
      },
    });
  }, [
    config,
    selectedCategory,
    fromTimeStamp,
    toTimeStamp,
    page,
    rowsPerPage,
    dispatch,
  ]);

  useEffect(() => {
    dispatch({type: CLEAR_DATA_GUARD_FILTERS})
    setPage(0);
    setRowsPerPage(10);
    setOrder("asc");
    setOrderBy(null);
    setRevokePopup(false);
    setRevokeDetails(null);
    setNotificationPopup(false);
    setNotificationDetails(null);
    setAlertActionPopup(false);
    setAlertActionDetails(null);
    setColumnId(null);
    setFilterAnchorEl(null);
    setFilterColumnField(null);
    setActiveFilterField(null);
  },[selectedCategory])


  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
  };

  const setSelectedCategory = (selected) => {
    setPage(0);
    props.history.push(`/tenant/dataguard/details/${selected}`);
  };

  const toggleColumnId = (columnId) => {
    setColumnId((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  useEffect(() => {
    setColumnId([]);
    config.table.columns.map((column) => toggleColumnId(column.id));
  }, [selectedCategory]);

  if (!config) {
    return <Typography variant="h6">Invalid page type</Typography>;
  }

  // Main render
  return (
    <div className="details-page-container">
      {loader && (
        <div className="data-guard-details-mainLoader-backdrop">
          <img
            src={Page_Loader}
            alt="Loading..."
            className="data-guard-mainLoader"
          />
        </div>
      )}

      <DetailPageHeader
        selected={selectedCategory}
        setSelected={setSelectedCategory}
      >
        <Paper className="details-table-paper">
          <TableContainer
            style={{
              maxHeight: "55vh",
              overflow: "auto",
              borderRadius: "15px",
            }}
          >
            <Table stickyHeader>
              <TableHead className="details-table-header">
                <TableRow>
                  {config.table.columns.map((column) => (
                    <TableCell
                      align={column.align || "center"}
                      key={column.id}
                      style={{
                        width: column.width,
                        backgroundColor: "#2145f3",
                        color: "white",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {column.sorting ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleSort(column.id)}
                          style={{ color: "white" }}
                        >
                          {column.renderHeader
                            ? column.renderHeader()
                            : column.title}
                        </TableSortLabel>
                      ) : column.renderHeader ? (
                        column.renderHeader()
                      ) : (
                        column.title
                      )}
                    </TableCell>
                  ))}
                  {!ACTION_NOT_NEEDED.includes(selectedCategory) && <TableCell
                    padding="none"
                    style={{
                      width: 40,
                      backgroundColor: "#2145f3",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  />}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedData?.map((row, index) => (
                  <ExpandableTableRow
                    key={index}
                    row={row}
                    columns={config.table.columns}
                    selectedCategory={selectedCategory}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {config.table.pagination && (
            <TablePagination
              style={{overflow:"visible"}}
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={data?.totalElements || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Paper>
      </DetailPageHeader>

      <DetailsPopup />
      <DataGuardAlertStatusSnackBar />
      {renderRevokePopup(
        revokePopup,
        revokeDetails,
        handleRevokePopupClose,
        handleRevokeConfirm,
        "default"
      )}
      {renderNotificationPopup(
        notificationPopup,
        notificationDetails,
        handleNotificationPopupClose,
        handleNotificationConfirm
      )}
      {renderAlertActionPopup(
        alertActionPopup,
        alertActionDetails,
        handleAlertActionPopupClose,
        handleAlertActionConfirm
      )}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <DataGuardFilter
          popupClose={handleCloseFilter}
          filterField={filterColumnField}
          selectedCategory={selectedCategory}
          config={config}
          fromTimeStamp={fromTimeStamp}
          toTimeStamp={toTimeStamp}
          rowsPerPage={rowsPerPage}
          page={page}
          columnId={[filterColumnField]}
          isIncognito={isIncognito}
        />
      </Popover>
    </div>
  );
};

export default DetailPage;
